/**
 * Background sync handlers for offline draft queue.
 * Called when the browser regains connectivity or on page focus.
 *
 * Processes pending billing/vitals/opd-checkin drafts one by one,
 * marks each synced or failed, and dispatches a custom event so
 * the UI can update its "offline queue" badge.
 */

import {
  getPendingDrafts,
  updateDraftStatus,
  deleteDraft,
  countPendingDrafts,
  type OfflineDraft,
} from './db';

const API_BASE = '/api/v1';

// ── Endpoint mapping per draft type ──────────────────────────────
const SYNC_ENDPOINTS: Record<OfflineDraft['type'], string> = {
  billing:     `${API_BASE}/billing/invoices`,
  vitals:      `${API_BASE}/opd/vitals`,
  opd_checkin: `${API_BASE}/opd/checkin`,
};

async function syncDraft(draft: OfflineDraft): Promise<void> {
  const url = SYNC_ENDPOINTS[draft.type];

  const response = await fetch(url, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(draft.payload),
    credentials: 'include',
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${body.slice(0, 200)}`);
  }
}

// ── Main sync runner ──────────────────────────────────────────────
let _syncInProgress = false;

export async function syncOfflineQueue(): Promise<{ synced: number; failed: number }> {
  if (_syncInProgress) return { synced: 0, failed: 0 };
  _syncInProgress = true;

  let synced = 0;
  let failed = 0;

  try {
    const pending = await getPendingDrafts();

    for (const draft of pending) {
      if (!draft.local_id) continue;

      // Skip permanently failed items (>= 5 retries)
      if (draft.retry_count >= 5) {
        await updateDraftStatus(draft.local_id, 'failed', 'Max retries exceeded');
        failed++;
        continue;
      }

      await updateDraftStatus(draft.local_id, 'syncing');

      try {
        await syncDraft(draft);
        await deleteDraft(draft.local_id);
        synced++;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        await updateDraftStatus(draft.local_id, 'failed', msg);
        failed++;
      }
    }
  } finally {
    _syncInProgress = false;
  }

  // Notify the UI
  const remaining = await countPendingDrafts();
  window.dispatchEvent(new CustomEvent('offline-queue-updated', {
    detail: { synced, failed, pending: remaining },
  }));

  return { synced, failed };
}

// ── Register online event listener ───────────────────────────────
export function registerSyncListeners(): () => void {
  const onOnline = () => {
    console.log('[offline-sync] Connection restored — syncing queue…');
    syncOfflineQueue().catch(console.error);
  };

  const onFocus = () => {
    // Sync on tab focus in case the online event was missed
    if (navigator.onLine) {
      syncOfflineQueue().catch(console.error);
    }
  };

  window.addEventListener('online',  onOnline);
  window.addEventListener('focus',   onFocus);

  // Run once immediately if already online
  if (navigator.onLine) {
    syncOfflineQueue().catch(console.error);
  }

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('focus',  onFocus);
  };
}

// ── Online status hook helper ─────────────────────────────────────
export function isOnline(): boolean {
  return navigator.onLine;
}
